import { useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';

export function useTableDataViewController<T>(tableItems: T[]) {
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [scrollState, updateScrollState] = useImmer({
    syncWidth: '100%',
    showLeftArrow: false,
    showRightArrow: false,
  });

  const updateWidth = () => {
    const container = containerRef.current;
    if (!container) return;

    updateScrollState((draft) => {
      draft.syncWidth = `${container.scrollWidth}px`;
    });
  };

  const checkArrows = () => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    updateScrollState((draft) => {
      draft.showLeftArrow = scrollLeft > 0;
      draft.showRightArrow = Math.ceil(scrollLeft) < scrollWidth - clientWidth;
    });
  };

  useEffect(() => {
    updateWidth();
    checkArrows();

    const onContainerScroll = () => {
      if (headerRef.current && containerRef.current) {
        headerRef.current.scrollLeft = containerRef.current.scrollLeft;
        checkArrows();
      }
    };

    const onHeaderScroll = () => {
      if (headerRef.current && containerRef.current) {
        containerRef.current.scrollLeft = headerRef.current.scrollLeft;
      }
    };

    const onResize = () => {
      updateWidth();
      checkArrows();
    };

    containerRef.current?.addEventListener('scroll', onContainerScroll);
    headerRef.current?.addEventListener('scroll', onHeaderScroll);
    window.addEventListener('resize', onResize);

    return () => {
      containerRef.current?.removeEventListener('scroll', onContainerScroll);
      headerRef.current?.removeEventListener('scroll', onHeaderScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [tableItems]);

  const scrollBy = (direction: number) => {
    const container = containerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * direction;
    container.scrollBy({ left: scrollAmount, behavior: 'auto' });
    checkArrows();
  };

  return {
    uiState: scrollState,
    actions: { scrollBy },
    references: {
      containerRef,
      headerRef,
    },
  };
}
